/**
 * StateDialog - create or edit a single `WorkflowState`.
 *
 * State details: key (auto-derived from label on create), label,
 * color (hex), is_initial, is_terminal, order, plus availability rules.
 *
 * `is_initial` is mutually exclusive across states on the same
 * version; the backend service clears the previous initial in the
 * same transaction so the UI does not need to juggle that state.
 */

import { useCallback, useMemo } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import {
  useCreateState,
  useUpdateState,
  type WorkflowStateAdmin,
} from '@/admin/hooks/use-workflow-definitions'
import { toastApiError } from '@/shared/lib/api-error'

import { RulesSection } from './rules-section'
import { StateSideEffectsSection } from './state-side-effects-section'

interface StateDialogProps {
  open: boolean
  onOpenChange: (next: boolean) => void
  mode: 'create' | 'edit'
  workflowUuid: string
  workflowKey: string
  resourceType?: string
  allStates: WorkflowStateAdmin[]
  state?: WorkflowStateAdmin
  defaultOrder?: number
}

const KEY_REGEX = /^[a-z][a-z0-9_]*$/

type StateFormValues = {
  key: string
  label: string
  color: string
  role_label: string
  is_initial: boolean
  is_terminal: boolean
  order: number
}

export function StateDialog({
  open,
  onOpenChange,
  mode,
  workflowUuid,
  workflowKey,
  resourceType,
  allStates,
  state,
  defaultOrder = 0,
}: StateDialogProps) {
  const create = useCreateState(workflowUuid)
  const update = useUpdateState(workflowUuid)

  const initial = useMemo<StateFormValues>(
    () => ({
      key: state?.key ?? '',
      label: state?.label ?? '',
      color: state?.color ?? '#6b7280',
      role_label: state?.role_label ?? '',
      is_initial: state?.is_initial ?? false,
      is_terminal: state?.is_terminal ?? false,
      order: state?.order ?? defaultOrder,
    }),
    [state, defaultOrder],
  )

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'label',
        label: 'Label',
        value: initial.label,
        required: 'Label is required',
        className: 'col-span-2',
      },
      {
        type: 'text',
        name: 'key',
        label: 'Key',
        placeholder: 'snake_case',
        value: initial.key,
        validationSchema: z.string()
          .trim()
          .min(1, 'Key is required')
          .regex(
            KEY_REGEX,
            'Use lowercase letters, digits, and underscores. Must start with a letter.',
          ),
        className: 'col-span-1',
      },
      {
        type: 'text',
        name: 'color',
        label: 'Color',
        placeholder: '#6b7280',
        value: initial.color,
        className: 'col-span-1',
      },
      {
        type: 'text',
        name: 'role_label',
        label: 'Role label',
        placeholder: 'e.g. RS, Lab, SS',
        value: initial.role_label,
        className: 'col-span-1',
      },
      {
        type: 'number',
        name: 'order',
        label: 'Order',
        value: initial.order,
        className: 'col-span-1',
      },
      {
        type: 'checkbox',
        name: 'is_initial',
        label: 'Initial state',
        value: initial.is_initial,
        className: 'col-span-1',
      },
      {
        type: 'checkbox',
        name: 'is_terminal',
        label: 'Terminal state',
        value: initial.is_terminal,
        className: 'col-span-1',
      },
    ],
    [initial],
  )

  const { Form, ref } = useForm(
    {
      name: `workflow-state-${mode}`,
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as unknown as StateFormValues
      const payload = {
        key: values.key.trim(),
        label: values.label.trim(),
        color: (values.color ?? '').trim() || '#6b7280',
        role_label: (values.role_label ?? '').trim(),
        is_initial: Boolean(values.is_initial),
        is_terminal: Boolean(values.is_terminal),
        order: Number(values.order) || 0,
      }
      try {
        if (mode === 'edit' && state) {
          await update.mutateAsync({ uuid: state.uuid, ...payload })
          toast.success('State updated')
        } else {
          await create.mutateAsync(payload)
          toast.success('State created')
        }
        onOpenChange(false)
      } catch (err) {
        toastApiError(err, `Failed to ${mode} state`)
      }
    },
    [open, mode, state?.uuid, fields],
  )

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        ref.current?.resetForm()
      }
      onOpenChange(next)
    },
    [onOpenChange, ref],
  )

  const busy = create.isPending || update.isPending

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[720px]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'edit' ? 'Edit State' : 'New State'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex max-h-[70vh] flex-col gap-6 overflow-y-auto pr-1">
          <Form className="grid grid-cols-2 gap-4 py-2" />
          <RulesSection
            stateUuid={state?.uuid}
            workflowUuid={workflowUuid}
            workflowKey={workflowKey}
            resourceType={resourceType}
            states={allStates}
          />
          <StateSideEffectsSection
            key={state?.uuid ?? 'new-state-side-effects'}
            workflowUuid={workflowUuid}
            state={state}
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            onClick={() => ref.current?.submitForm()}
            disabled={busy}
            data-testid="workflows-state-submit"
          >
            {busy ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
